import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/contexts/NotificationContext";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import { translateAuthError } from "@/lib/auth-error-messages";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CheckCircle, XCircle, Loader2, Info } from "lucide-react";
import { useTranslation } from "react-i18next";

const EmailConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const { user } = useAuth();
  const [status, setStatus] = useState<
    "loading" | "success" | "error" | "info"
  >("loading");
  const [message, setMessage] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    const processEmailConfirmation = async () => {
      // Получаем параметры из query string
      const token = searchParams.get("token");
      const tokenHash = searchParams.get("token_hash");
      const type = searchParams.get("type");
      let urlMessage = searchParams.get("message");
      const error = searchParams.get("error");
      const errorCode = searchParams.get("error_code");
      const errorDescription = searchParams.get("error_description");

      // Если message нет в query string, проверяем hash fragment
      if (!urlMessage) {
        const hash = window.location.hash;
        const hashParams = new URLSearchParams(hash.substring(1));
        urlMessage = hashParams.get("message");
      }

      // Отладочная информация
      console.log("Email confirmation debug:", {
        fullUrl: window.location.href,
        hash: window.location.hash,
        hashEmpty: window.location.hash === "#",
        searchParams: Object.fromEntries(searchParams),
        urlMessage,
        token,
        tokenHash,
        type,
        error,
        allParamsEmpty: !urlMessage && !token && !tokenHash && !type && !error,
      });

      // Специальная обработка для случая успешного завершения второго этапа
      // URL вида: https://app.voice2doc.com/email-confirmation# или без параметров
      if (
        !urlMessage &&
        !token &&
        !tokenHash &&
        !type &&
        !error &&
        (window.location.hash === "#" || window.location.hash === "")
      ) {
        setStatus("success");
        setMessage(t('auth.email_confirm.success_message_complete'));

        if (user) {
          addNotification({
            userId: user.id,
            type: "success",
            message: t('auth.email_confirm.notification_success'),
          });
        }
        return;
      }

      // Обработка ошибок в URL
      if (error) {
        setStatus("error");
        const errorMsg =
          errorDescription || error || t('auth.email_confirm.error_processing');
        const translatedMessage = translateAuthError(errorMsg);
        setMessage(translatedMessage);

        if (user) {
          addNotification({
            userId: user.id,
            type: "error",
            message: translatedMessage,
          });
        }
        return;
      }

      // Обработка информационных сообщений от Supabase
      if (urlMessage) {
        const decodedMessage = decodeURIComponent(
          urlMessage.replace(/\+/g, " ")
        );

        if (
          decodedMessage.includes(
            "Please proceed to confirm link sent to the other email"
          ) ||
          decodedMessage.includes(
            "Confirmation link accepted. Please proceed to confirm link sent to the other email"
          )
        ) {
          setStatus("info");
          setMessage(t('auth.email_confirm.info_message_first_step'));

          if (user) {
            addNotification({
              userId: user.id,
              type: "info",
              message: t('auth.email_confirm.notification_info'),
            });
          }
          return;
        } else if (decodedMessage.includes("Email change confirmed")) {
          setStatus("success");
          setMessage(t('auth.email_confirm.success_message_complete'));

          if (user) {
            addNotification({
              userId: user.id,
              type: "success",
              message: t('auth.email_confirm.notification_success'),
            });
          }
          return;
        } else {
          // Другие сообщения от Supabase
          setStatus("info");
          setMessage(decodedMessage);
          return;
        }
      }

      // Проверяем наличие токена для обработки
      const tokenToUse = tokenHash || token;

      if (!tokenToUse || !type) {
        // Если нет токена, но есть сообщение от Supabase, не показываем ошибку
        if (urlMessage) {
          setStatus("info");
          setMessage(t('auth.email_confirm.error_no_token_with_message'));
          return;
        }

        setStatus("error");
        setMessage(t('auth.email_confirm.error_invalid_link'));
        return;
      }

      // Если есть токен, пытаемся обработать подтверждение
      if (type === "email_change") {
        try {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenToUse,
            type: "email_change",
          });

          if (error) {
            console.error("Ошибка подтверждения email:", error);

            const translatedMessage = translateAuthError(error.message);
            setStatus("error");
            setMessage(translatedMessage);

            if (user) {
              addNotification({
                userId: user.id,
                type: "error",
                message: translatedMessage,
              });
            }
          } else {
            setStatus("success");
            setMessage(t('auth.email_confirm.success_message_complete'));

            if (user) {
              addNotification({
                userId: user.id,
                type: "success",
                message: t('auth.email_confirm.notification_success'),
              });
            }
          }
        } catch (error) {
          console.error("Ошибка при подтверждении email:", error);
          setStatus("error");
          setMessage(t('auth.email_confirm.error_processing'));

          if (user) {
            addNotification({
              userId: user.id,
              type: "error",
              message: t('auth.email_confirm.notification_error'),
            });
          }
        }
      } else {
        setStatus("error");
        setMessage(t('auth.email_confirm.error_unsupported'));
      }
    };

    processEmailConfirmation();
  }, [searchParams, addNotification, user, t]);

  const handleGoToProfile = () => {
    navigate("/profile");
  };

  const handleGoToDashboard = () => {
    navigate("/dashboard");
  };

  const handleTestCurrentUrl = () => {
    console.log("Current URL analysis:");
    console.log("Full URL:", window.location.href);
    console.log("Hash:", window.location.hash);
    console.log("Hash empty:", window.location.hash === "#");
    console.log("Hash completely empty:", window.location.hash === "");
    console.log("Search:", window.location.search);

    // Тестируем различные URL
    const testUrls = [
      "https://app.voice2doc.com/email-confirmation#message=Confirmation+link+accepted.+Please+proceed+to+confirm+link+sent+to+the+other+email",
      "https://app.voice2doc.com/email-confirmation#",
      "https://app.voice2doc.com/email-confirmation",
    ];

    testUrls.forEach((testUrl) => {
      console.log("Test URL:", testUrl);
      const url = new URL(testUrl);
      const hashParams = new URLSearchParams(url.hash.substring(1));
      console.log("Hash params:", Object.fromEntries(hashParams));
      console.log("Hash:", url.hash);
    });
  };

  const getIcon = () => {
    switch (status) {
      case "loading":
        return <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />;
      case "success":
        return <CheckCircle className="h-12 w-12 text-green-500" />;
      case "error":
        return <XCircle className="h-12 w-12 text-red-500" />;
      case "info":
        return <Info className="h-12 w-12 text-blue-500" />;
      default:
        return <Loader2 className="h-12 w-12 text-blue-500 animate-spin" />;
    }
  };

  const getTitle = () => {
    switch (status) {
      case "loading":
        return t('auth.email_confirm.title');
      case "success":
        return t('auth.email_confirm.success_title');
      case "error":
        return t('auth.email_confirm.error_title');
      case "info":
        return t('auth.email_confirm.info_title');
      default:
        return t('auth.email_confirm.title');
    }
  };

  const getDescription = () => {
    if (status === "loading") {
      return t('auth.email_confirm.processing');
    }
    return message;
  };

  const renderMessage = () => {
    return message.split("\n").map((line, index) => (
      <span key={index}>
        {line}
        {index < message.split("\n").length - 1 && <br />}
      </span>
    ));
  };

  return (
    <div className="min-h-screen bg-muted flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">{getIcon()}</div>
          <CardTitle className="text-2xl">{getTitle()}</CardTitle>
          <CardDescription className="whitespace-pre-line">
            {status === "loading" ? getDescription() : renderMessage()}
          </CardDescription>
        </CardHeader>

        {status !== "loading" && (
          <CardContent className="space-y-4">
            <div className="flex flex-col space-y-2">
              <Button onClick={handleGoToProfile} className="w-full">
                {t('auth.email_confirm.go_profile')}
              </Button>
              <Button
                onClick={handleGoToDashboard}
                variant="outline"
                className="w-full"
              >
                {t('auth.email_confirm.go_home')}
              </Button>
              {process.env.NODE_ENV === "development" && (
                <Button
                  onClick={handleTestCurrentUrl}
                  variant="outline"
                  size="sm"
                  className="w-full mt-4 text-xs"
                >
                  🔍 Debug URL (dev only)
                </Button>
              )}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default EmailConfirmation;
